import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Box, Button, Container, Divider, Paper, TextField, Typography, Alert, Link as MuiLink, MenuItem } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { clientRegister, clientGoogleLogin, clientCheckAuth } from "../../../store/thunks/clientAuthThunks";
import { SAT_CLIENT_FISCAL_REGIMES_ARRAY } from "hvp-shared";
import { CfdiUseSelect } from "../../../components/billing/CfdiUseSelect";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

/**
 * Registration asks no password (GH126): the password is chosen through a link mailed to the email
 * typed here, and the account is created then — so every account's email is proven.
 */
const schema = z.object({
  rfc: z.string().trim().min(12, "RFC debe tener 12 o 13 caracteres").max(13, "RFC debe tener 12 o 13 caracteres"),
  contactEmail: z.email("Escribe un correo válido: ahí te enviamos el enlace"),
  businessName: z.string().trim().min(3, "Mínimo 3 caracteres"),
  fiscalRegime: z.string().min(1, "Requerido"),
  postalCode: z.string().trim().regex(/^\d{5}$/, "5 dígitos"),
  cfdiUse: z.string().min(1, "Requerido"),
  name: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export const RegisterPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { error, isLoading, isAuthenticated } = useAppSelector((state) => state.clientAuth);
  const [linkSentMessage, setLinkSentMessage] = useState<string>();

  useEffect(() => {
    dispatch(clientCheckAuth());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/facturacion", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const { control, handleSubmit, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      rfc: "", contactEmail: "", businessName: "",
      fiscalRegime: "", postalCode: "", cfdiUse: "G03", name: "",
    },
  });

  const watchedRegime = watch("fiscalRegime");
  const watchedRfc = watch("rfc");

  const onSubmit = async (data: FormData) => {
    const result = await dispatch(clientRegister(data));
    if (result?.linkSent) setLinkSentMessage(result.message);
  };

  if (linkSentMessage) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: "#0e368d" }}>
            Te enviamos un correo
          </Typography>
          <Alert severity="info" sx={{ my: 2 }}>{linkSentMessage}</Alert>
          <Typography variant="body2" align="center">
            <MuiLink component={Link} to="/facturacion/login">Volver a iniciar sesión</MuiLink>
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: "#0e368d" }}>
            Crear cuenta
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            Portal de Facturación - Hospital Veterinario Peninsular
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Datos de acceso</Typography>

            <Controller name="rfc" control={control} render={({ field }) => (
              <TextField {...field} label="RFC" fullWidth margin="dense" error={!!errors.rfc} helperText={errors.rfc?.message || "Tu RFC de 12 o 13 caracteres"} inputProps={{ style: { textTransform: "uppercase" } }} autoFocus />
            )} />
            <Controller name="contactEmail" control={control} render={({ field }) => (
              <TextField {...field} label="Correo" type="email" fullWidth margin="dense" error={!!errors.contactEmail} helperText={errors.contactEmail?.message || "Te enviamos un enlace para crear tu contraseña; también recibes ahí tus facturas"} autoComplete="email" />
            )} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Datos fiscales</Typography>

            <Controller name="businessName" control={control} render={({ field }) => (
              <TextField {...field} label="Razón social" fullWidth margin="dense" error={!!errors.businessName} helperText={errors.businessName?.message || "Exacto como aparece en tu constancia de situación fiscal"} />
            )} />
            <Controller name="fiscalRegime" control={control} render={({ field }) => (
              <TextField {...field} select label="Régimen fiscal" fullWidth margin="dense" error={!!errors.fiscalRegime} helperText={errors.fiscalRegime?.message}>
                {SAT_CLIENT_FISCAL_REGIMES_ARRAY.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="postalCode" control={control} render={({ field }) => (
              <TextField {...field} label="Código postal fiscal" fullWidth margin="dense" error={!!errors.postalCode} helperText={errors.postalCode?.message} />
            )} />
            <Controller name="cfdiUse" control={control} render={({ field }) => (
              <CfdiUseSelect
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                fiscalRegime={watchedRegime}
                rfc={watchedRfc}
                hint="Es tu uso habitual; al pedir una factura puedes elegir otro."
                error={!!errors.cfdiUse}
                errorMessage={errors.cfdiUse?.message}
              />
            )} />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Contacto (opcional)</Typography>

            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Nombre de contacto" fullWidth margin="dense" />
            )} />

            <Button type="submit" variant="contained" fullWidth size="large" disabled={isLoading} sx={{ mt: 2, mb: 2, backgroundColor: "#0e368d", "&:hover": { backgroundColor: "#0b2b71" } }}>
              {isLoading ? "Enviando..." : "Enviarme el enlace"}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>o</Divider>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse.credential) {
                  dispatch(clientGoogleLogin(credentialResponse.credential));
                }
              }}
              onError={() => console.error("Google login failed")}
              text="signup_with"
            />
          </Box>

          <Typography variant="body2" align="center">
            ¿Ya tienes cuenta?{" "}
            <MuiLink component={Link} to="/facturacion/login">
              Inicia sesión
            </MuiLink>
          </Typography>
        </Paper>
      </Container>
    </GoogleOAuthProvider>
  );
};
